import React, { PropsWithChildren } from "react";
import { createClasses } from "@bloomberg/media-ui-core";
import { ReactivationToast } from "@bloomberg/subscriptions-notifications";
import { TippyProvider } from "@bloomberg/tippy";
import { FastPathScript, FortressScript } from "@foundation/fortress";
import {
    FortressClientColorOptions,
    FortressClientDomain,
} from "@foundation/fortress/lib/types";
import { Tophat } from "@foundation/nextjs-tophat";
import { useStatsigClientWebSetup, StatsigProvider } from "@foundation/statsig";

import { ARTICLE_DATA_PAGE_TYPE } from "~constants";
import { NavAndContextualNav } from "~components/NavAndContextualNav";
import NavFooter from "~components/NavFooter";
import { ARTICLE_TYPE, LayoutProps, Story } from "~types/types";
import { getHydratedInitialPageProps } from "~utils/getHydratedInitialPageProps";

import styles from "./pageLayout.module.css";

export interface PageLayoutProps {
    props: {
        layout: LayoutProps;
        story: Story;
        countryCode?: string;
    };
    type: ARTICLE_TYPE;
}

export type PageLayoutChildrenProps = PropsWithChildren & PageLayoutProps;

const colorEnv = process.env.NEXT_PUBLIC_COLOR_ENV || "";
const fenceDomain = process.env.NEXT_PUBLIC_ASSET_HOST || "";

const ARTICLE_TYPE_LAYOUT_CLASSNAMES: Partial<Record<ARTICLE_TYPE, string>> = {
    feature: styles.featureContainer,
    explainers: styles.featureContainer,
};

const STATSIG_ENV_TIER =
    process.env.NEXT_PUBLIC_COLOR_ENV === "production"
        ? "production"
        : "staging";

export function PageLayout({
    type,
    ...page
}: PageLayoutChildrenProps): JSX.Element {
    const { children, props } = page;

    const { tophat, menuItems, footerMenuItems, breakingNews } =
        props.layout || getHydratedInitialPageProps()?.layout;
    const { story, countryCode } = props;

    // these "orange" overrides will be removed when
    // orange is no longer treated as production
    // do we have a ticket to do this?
    const modifiedFortressClientDomain = (
        ["production", "blue", "orange"].includes(colorEnv)
            ? FortressClientDomain.PRODUCTION
            : fenceDomain
    ) as FortressClientDomain;

    const mainContainerClass = createClasses([
        "main-container grid-layout-container",
        ARTICLE_TYPE_LAYOUT_CLASSNAMES[type],
    ]);

    const statsigClient = useStatsigClientWebSetup({
        environmentTier: STATSIG_ENV_TIER,
        statsigClientKey: process.env.NEXT_PUBLIC_STATSIG_CLIENT_KEY!,
    });

    return (
        <>
            <Tophat snippet={tophat} />
            <StatsigProvider client={statsigClient}>
                <NavAndContextualNav
                    breakingNews={breakingNews}
                    menuItems={menuItems}
                    story={story}
                    countryCode={countryCode}
                />
                <main>
                    <TippyProvider rootSelector={"article"}>
                        <div
                            className={mainContainerClass}
                            data-page-type={ARTICLE_DATA_PAGE_TYPE[type]}
                        >
                            {children}
                        </div>
                    </TippyProvider>
                </main>
            </StatsigProvider>
            <ReactivationToast />
            <FastPathScript
                fortressClientDomain={modifiedFortressClientDomain}
                color={colorEnv as FortressClientColorOptions | undefined}
            />
            <FortressScript
                fortressClientDomain={modifiedFortressClientDomain}
                color={colorEnv as FortressClientColorOptions | undefined}
            />
            <div className={styles.footerContainer}>
                <div className={styles.footer}>
                    <NavFooter footerMenuItems={footerMenuItems} />
                </div>
            </div>
        </>
    );
}
